import "common/form/form.scss";

import { PureComponent } from "react";
import PropTypes from "prop-types";
import { reduxForm, Field, SubmissionError } from "redux-form";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import FormGroup from "common/form/group";
import Button from "common/core/button";
import FormGroupErrors from "common/form/group_errors";
import TextField from "common/form/fields/text";
import DocumentUploaderManager from "common/document/uploader/manager";
import { interactingWithInput } from "common/form/util";
import { composeValidators } from "util/form";
import { useFeatureFlag } from "common/feature_gating";
import { onlyRequiresEsign } from "util/completion_requirements/completion_requirements_text";
import { PROOF_TRANSACTIONS } from "constants/feature_gates";
import { DESIGNATION_ROLES } from "constants/annotations";
import { validatePresence, validatePermalink } from "validators/form";
import { duplicatePermalink } from "errors/form";
import MultiSelectInput from "common/form/inputs/multi_select";
import RadioGroup from "common/form/inputs/radio_group";
import RadioItem from "common/form/inputs/radio_group/radio_item";
import {
  TemplateTagsEnum,
  OrganizationDocumentTemplateState,
  OrganizationTypeEnum,
  ProcessingStates,
} from "graphql_globals";

import TemplatesQuery from "../templates_query.graphql";
import { TEMPLATE_PROP_TYPE } from "../constants";
import { documentsInput } from "../util";
import ProcessingFailedModal from "./processing_failed_modal";
import TemplateDocumentUploader from "./uploader";
import { EasylinkPreviewSection } from "../../easylink_template_preview";

const templateTags = Object.keys(TemplateTagsEnum).map((tag) => {
  return {
    label: tag,
    value: tag,
  };
});

function validate(values) {
  return composeValidators(
    validatePresence({ field: "templateName", label: "Template Name" }),
    validatePermalink({ field: "permalink" }),
  )(values);
}

/** This private component is the start/top of the presentation components */
class OrganizationTemplateEditForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isSaving: false,
      isCancelling: false,
      processingDocumentIds: [],
      showProcessingFailedModal: false,
      encompassTags: props.template.encompassTags.map((tag) => {
        return {
          label: tag,
          value: tag,
        };
      }),
    };
  }

  componentDidMount() {
    this.mounted = true;
    const {
      template: { name, displayName, permalink, documentBundle },
      initialize,
    } = this.props;
    initialize({
      templateName: name,
      displayName,
      permalink,
      hack: "nonemptyfield", // to prevent the redux store values field from being cleared
    });

    // Set initial statae if any docs are still processing.  This is necessary
    // for new templates because the first doc will have just been uploaded
    // when this component mounts.
    const processingDocumentIds = documentBundle.documents.edges
      .filter((edge) => {
        return edge.document.processingState === ProcessingStates.PENDING;
      })
      .map((edge) => edge.document.id);
    this.setState({ processingDocumentIds });

    window.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  getBasePath = () => {
    const {
      location: { pathname },
    } = this.props;
    const basePath = pathname.split("/")[1];
    return basePath;
  };

  handleKeyDown = (e) => {
    if (interactingWithInput()) {
      return;
    }

    if (e.key === "Escape") {
      this.returnToList();
    }
  };

  updateTemplateTags = () => {
    const { template } = this.props;
    this.setState({ isSaving: true });
    this.props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id: template.id,
            activationState: template.activationState,
            name: template.name,
            displayName: template.displayName,
            permalink: template.permalink,
            encompassTags: this.state.encompassTags.map((tag) => tag.value),
          },
        },
      })
      .then(() => {
        this.setState({ isSaving: false });
      });
  };

  quietUpdateTemplate = (args) => {
    return this.updateTemplate({ ...args, quiet: true });
  };

  updateTemplateTools = (args) => {
    return this.updateTemplate({ ...args, nextPage: "tools" });
  };

  updateTemplateExit = (args) => {
    return this.updateTemplate({ ...args, nextPage: "exit" });
  };

  updateTemplate = ({ templateName, displayName, permalink, quiet, nextPage = "prepare" }) => {
    const {
      template,
      presetPermalink,
      location: { search },
      navigate,
      completeFn,
    } = this.props;
    const basePath = this.getBasePath();
    const { id, documentBundle, activationState } = template;
    const { updateCacheFn } = this;

    this.setState({ isSaving: true });

    const permalinkValue = presetPermalink || permalink;

    return this.props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            name: templateName,
            displayName,
            permalink: permalinkValue,
            activationState,
            encompassTags: this.state.encompassTags.map((tag) => tag.value),
          },
        },
        optimisticResponse: {
          updateOrganizationDocumentTemplate: {
            __typename: "UpdateOrganizationDocumentTemplateMutationPayload",
            errors: null,
            organizationDocumentTemplate: {
              ...template,
              name: templateName,
              displayName,
              permalink: permalinkValue,
            },
          },
        },
        updateCacheFn,
      })
      .then(() => {
        this.setState({ isSaving: false });
        if (quiet) {
          return;
        }
        if (completeFn) {
          completeFn();
        } else if (nextPage === "tools") {
          navigate(`/${basePath}/${template.id}/tools/${search}`);
        } else if (nextPage === "exit") {
          const wasSearching = this.props.location.state;
          if (wasSearching) {
            this.returnToQuery();
          } else {
            this.returnToList();
          }
        } else {
          // skip the annotation screen if no documents in the bundle
          if (documentBundle.documents.edges.length === 0) {
            navigate(`/${basePath}`);
          } else {
            navigate(`/${basePath}/${id}${search}`);
          }
        }
      })
      .catch((error) => {
        this.setState({ isSaving: false });
        const { graphQLErrors } = error;
        if (
          graphQLErrors &&
          graphQLErrors.length > 0 &&
          graphQLErrors[0].message === "non_unique_permalink"
        ) {
          throw new SubmissionError({
            permalink: duplicatePermalink({ permalink }),
          });
        } else {
          throw error;
        }
      });
  };

  updateActivationState = (value) => {
    const { template } = this.props;
    this.props.updateTemplateMutateFn({
      variables: {
        input: {
          id: template.id,
          activationState: value,
          name: template.name,
          displayName: template.displayName,
          permalink: template.permalink,
          encompassTags: this.state.encompassTags.map((tag) => tag.value),
        },
      },
    });
  };

  updateDocuments = (newDocumentEdges) => {
    const { template, presetPermalink } = this.props;
    const { id, name, displayName, permalink, documentBundle, activationState } = template;
    const { updateCacheFn } = this;

    this.setState({ isSaving: true });

    return this.props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            documents: documentsInput(newDocumentEdges),
            permalink: presetPermalink || permalink,
            activationState,
            name,
            displayName,
          },
        },
        optimisticResponse: {
          updateOrganizationDocumentTemplate: {
            __typename: "UpdateOrganizationDocumentTemplateMutationPayload",
            errors: null,
            organizationDocumentTemplate: {
              ...template,
              documentBundle: {
                ...documentBundle,
                documents: {
                  __typename: "DocumentConnection",
                  edges: newDocumentEdges,
                },
              },
            },
          },
        },
        updateCacheFn,
      })
      .then(() => {
        this.setState({ isSaving: false });
      });
  };

  updateCacheFn = () => {
    return function update(cacheProxy, { data: { updateOrganizationDocumentTemplate } }) {
      const data = cacheProxy.readQuery({
        query: TemplatesQuery,
      });
      const updatedEdge = {
        __typename: "OrganizationDocumentTemplate",
        template: updateOrganizationDocumentTemplate.organizationDocumentTemplate,
      };

      // newly added template edge may not exist in the data yet,
      // so we update the edge if it exists and add it if it doesn't
      const edgeExists = data.organization.templates.edges.find(
        (edge) => edge.template.id === updatedEdge.id,
      );
      const updater = edgeExists
        ? (edges) => edges.map((edge) => (edge.template.id === updatedEdge.id ? updatedEdge : edge))
        : (edges) => [updatedEdge, ...edges];
      const newData = {
        ...data,
        organization: {
          ...data.organization,
          templates: {
            ...data.organization.templates,
            edges: updater(data.organization.templates.edges),
          },
        },
      };
      cacheProxy.writeQuery({ query: TemplatesQuery, data: newData });
    };
  };

  returnToList = () => {
    const {
      location: { search },
      navigate,
    } = this.props;
    const basePath = this.getBasePath();
    navigate(`/${basePath}${search}`);
  };

  returnToQuery = () => {
    const {
      location: { state },
      navigate,
    } = this.props;
    const basePath = this.getBasePath();
    navigate(`/${basePath}?search=${state}`);
  };

  lowerCasePermalink = (value) => {
    return value.toLowerCase();
  };

  deleteTemplate = () => {
    const {
      template: { id },
      deleteTemplateMutateFn,
    } = this.props;

    this.setState({ isCancelling: true });

    deleteTemplateMutateFn({
      variables: { input: { id } },
      optimisticResponse: {
        deleteOrganizationDocumentTemplate: {
          __typename: "DeleteOrganizationDocumentTemplateMutationPayload",
          errors: null,
        },
      },
    })
      .then(() => {
        this.setState({ isCancelling: false });
        this.returnToList();
      })
      .catch(() => this.setState({ isCancelling: false }));
  };

  addDocument = (obj, onSuccess, onFailure) => {
    const {
      template: {
        id,
        permalink,
        documentBundle: { documents },
        name,
        displayName,
        activationState,
      },
      presetPermalink,
    } = this.props;
    const { updateCacheFn } = this;

    this.setState({ isSaving: true });

    const newDocuments = documentsInput(documents.edges).concat({
      file_handle: obj.s3Key,
      name: obj.filename,
      customer_can_annotate: true,
      witness_required: false,
      notarization_required: true,
      proofing_required: false,
      esign: false,
    });

    return this.props
      .updateTemplateMutateFn({
        variables: {
          input: {
            id,
            documents: newDocuments,
            permalink: presetPermalink || permalink,
            name,
            displayName,
            activationState,
          },
        },
        updateCacheFn,
      })
      .then((response) => {
        this.setState({ isSaving: false });
        const { organizationDocumentTemplate, errors } =
          response.data.updateOrganizationDocumentTemplate;
        if (errors) {
          onFailure(errors);
        } else {
          onSuccess(organizationDocumentTemplate);
        }
      });
  };

  handleDocumentProcessingStarted = (documentId) => {
    if (!this.mounted) {
      return; // callback is not needed if unmounted
    }
    this.setState((prevState) => {
      const processingDocumentIds = prevState.processingDocumentIds;
      processingDocumentIds.push(documentId);
      return { processingDocumentIds };
    });
  };

  handleDocumentProcessingDone = (documentId) => {
    if (!this.mounted) {
      return; // callback is not needed if unmounted
    }
    this.setState((prevState) => {
      const processingDocumentIds = prevState.processingDocumentIds;
      return {
        processingDocumentIds: processingDocumentIds.filter((id) => id !== documentId),
      };
    });
  };

  handleDocumentProcessingFailed = (documentId) => {
    if (!this.mounted) {
      return; // callback is not needed if unmounted
    }

    // Backend does not delete the doc from the bundle automatically, so we do it here
    const {
      template: {
        documentBundle: { documents },
      },
    } = this.props;
    const docEdge = documents.edges.find((edge) => edge.document.id === documentId);
    this.handleDeleteDocument(docEdge.document);

    this.setState((prevState) => {
      const processingDocumentIds = prevState.processingDocumentIds;
      return {
        processingDocumentIds: processingDocumentIds.filter((id) => id !== documentId),
        showProcessingFailedModal: true, // Inform the user that the doc is not processable
      };
    });
  };

  closeProcessingFailedModal = () => {
    this.setState({ showProcessingFailedModal: false });
  };

  handleChangeDocumentProperties = (propValues, document) => {
    const {
      template: {
        documentBundle: { documents },
      },
    } = this.props;
    const documentId = document.id;
    const newDocumentEdges = documents.edges.map((edge) => {
      return edge.document.id === documentId
        ? { ...edge, document: { ...edge.document, ...propValues } }
        : edge;
    });
    this.updateDocuments(newDocumentEdges);
  };

  handleDeleteDocument = (document) => {
    const {
      template: {
        documentBundle: { documents },
      },
    } = this.props;
    const documentId = document.id;
    const newDocumentEdges = documents.edges.filter((edge) => {
      return edge.document.id !== documentId;
    });
    this.updateDocuments(newDocumentEdges);
  };

  handleRenameDocument = (document, newName) => {
    const {
      template: {
        documentBundle: { documents },
      },
    } = this.props;
    const documentId = document.id;
    const newDocumentEdges = documents.edges.map((edge) => {
      return edge.document.id === documentId
        ? { ...edge, document: { ...edge.document, name: newName } }
        : edge;
    });
    this.updateDocuments(newDocumentEdges);
  };

  hasMultipleSigners() {
    const designationSignerIndicies =
      this.props.template.documentBundle.documentDesignationJunctions
        .filter((junction) => junction.signerRole.role === DESIGNATION_ROLES.SIGNER)
        .map((junction) => junction.signerRole.index);
    return new Set(designationSignerIndicies).size > 1;
  }

  render() {
    const { organization, template, handleSubmit, location, isMortgage } = this.props;
    const { isSaving, isCancelling, processingDocumentIds, showProcessingFailedModal } = this.state;
    const documents = template.documentBundle.documents.edges.map((edge) => edge.document);
    const isNewTemplate = location.pathname.endsWith("/new");
    const canRequireMeeting =
      organization.organizationType === OrganizationTypeEnum.LENDER ||
      organization.organizationType === OrganizationTypeEnum.TITLE_AGENCY;
    const isProcessing = processingDocumentIds.length > 0;
    const isEsign = onlyRequiresEsign(template.documentBundle.completionRequirements);
    const templateStates = [
      OrganizationDocumentTemplateState.ACTIVE,
      OrganizationDocumentTemplateState.DRAFT,
      OrganizationDocumentTemplateState.DISABLED,
    ];

    const cancelButtonClicked = () => {
      const wasSearching = this.props.location.state;
      if (isNewTemplate) {
        this.deleteTemplate();
      } else if (wasSearching) {
        this.returnToQuery();
      } else {
        this.returnToList();
      }
    };
    // HACK ALERT
    // This extra hidden field is necessary because the redux form values key disappears from
    // the redux store if you have a single field form and the value is cleared.
    // If there are no values, validation is not performed, so no error will be shown even though
    // the field is required. Adding an extra hidden field prevents the values key from disappearing.
    // This is used in conjunction with initializing a value for this field, above.

    // NOTE: this seems incredibly incorrect. if you come by in the future,
    // you can probably fix this with the our new Field components
    const emptyField = <Field name="hack" component={() => null} />;

    return (
      <form className="OrganizationTemplateEdit--Form">
        {showProcessingFailedModal && (
          <ProcessingFailedModal onClose={this.closeProcessingFailedModal} />
        )}

        {template.preset && (
          <Button
            onClick={handleSubmit(this.updateTemplateTools)}
            variant="secondary"
            buttonColor="action"
            className="OrganizationTemplateEdit--Form--Button"
            automationId="templates-developer-tools"
          >
            <FormattedMessage
              id="ef057505-3d3d-4dea-b233-f7b71bcaddbf"
              defaultMessage="Template Settings"
            />
          </Button>
        )}

        {template.preset && (
          <div className="OrganizationTemplateTools--Header--Toggle">
            <RadioGroup
              selectedValue={template.activationState}
              onChange={this.updateActivationState}
            >
              {templateStates.map((state) => (
                <RadioItem key={state} value={state} label={state} />
              ))}
            </RadioGroup>
          </div>
        )}

        <FormGroup
          fields={["templateName"]}
          disableFormRowStyle
          className="OrganizationTemplateEdit--Form--Group"
        >
          {emptyField}
          <TextField
            name="templateName"
            onBlur={this.props.quietUpdate ? handleSubmit(this.quietUpdateTemplate) : null}
            useStyledInput
            placeholder="e.g. Power of Attorney for notarization"
            label="Template Name"
            data-automation-id="template-name-field"
          />
          <FormGroupErrors fields={["templateName"]} />
        </FormGroup>

        {template.preset && (
          <FormGroup
            fields={["displayName"]}
            disableFormRowStyle
            className="OrganizationTemplateEdit--Form--Group"
          >
            <TextField
              name="displayName"
              onBlur={this.props.quietUpdate ? handleSubmit(this.quietUpdateTemplate) : null}
              useStyledInput
              label="Display Name (optional)"
              data-automation-id="template-display-name-field"
            />
          </FormGroup>
        )}

        {!this.props.presetPermalink && !template.preset && (
          <FormGroup
            fields={["permalink"]}
            disableFormRowStyle
            className="OrganizationTemplateEdit--Form--Group"
          >
            <TextField
              id="permalink"
              name="permalink"
              onBlur={this.props.quietUpdate ? handleSubmit(this.quietUpdateTemplate) : null}
              useStyledInput
              label="Permalink for API use (optional)"
              normalize={this.lowerCasePermalink}
              data-automation-id="permalink-field"
            />

            <FormGroupErrors fields={["permalink"]} />
          </FormGroup>
        )}

        {template.preset && (
          <>
            <div className="OrganizationTemplateEdit--TemplateTags">
              <MultiSelectInput
                items={templateTags}
                onChange={(tags) => {
                  this.setState({ encompassTags: tags });
                }}
                value={this.state.encompassTags}
                label="Template Tags"
                data-automation-id="save-template-tags"
              />
            </div>
          </>
        )}

        <div className="OrganizationTemplateEdit--Uploader">
          <DocumentUploaderManager
            documents={documents}
            selectedDocuments={{}}
            onChangeDocumentProperties={this.handleChangeDocumentProperties}
            onDeleteDocuments={this.handleDeleteDocument}
            onRenameDocument={this.handleRenameDocument}
            showWitnessRequired={!isEsign}
            onDocumentProcessingStarted={this.handleDocumentProcessingStarted}
            onDocumentProcessingDone={this.handleDocumentProcessingDone}
            onDocumentProcessingFailed={this.handleDocumentProcessingFailed}
            disableClick
            hideAnnotateButton
            canRequireEsign={!canRequireMeeting}
            canRequireMeeting={canRequireMeeting}
            organization={organization}
            isMortgage={isMortgage}
            esignAuthEnabled={this.props.esignAuthEnabled}
            isTemplateEditing
          />
        </div>

        <TemplateDocumentUploader
          title="+ Add Document"
          buttonClassName="OrganizationTemplateEdit--Uploader--Button"
          onAddDocument={this.addDocument}
          organizationId={organization.id}
        />

        {template.preset && (
          <div className="OrganizationTemplateInformationalText">
            {
              <span>
                To place signer fields on your document template, choose “Prepare Document.”
                Otherwise, "Save and Exit."
              </span>
            }
          </div>
        )}

        {template.easylinks.length > 0 && <EasylinkPreviewSection easylinks={template.easylinks} />}

        <div className="OrganizationTemplateEdit--options">
          {!this.props.hideCancel && (
            <Button
              automationId="cancel-button"
              isLoading={isCancelling}
              disabled={isCancelling}
              onClick={cancelButtonClicked}
              variant="tertiary"
              buttonColor="action"
            >
              <FormattedMessage id="75b26d71-b425-4205-9d16-56bb830de3b5" defaultMessage="Cancel" />
            </Button>
          )}

          {!this.props.hideSaveAndExit && (
            <Button
              onClick={handleSubmit(this.updateTemplateExit)}
              isLoading={isSaving || isCancelling}
              disabled={isSaving || isCancelling}
              automationId="save-exit-button"
              className="OrganizationTemplateEdit--Uploader--SaveExitButton"
              variant="secondary"
              buttonColor="action"
            >
              {this.props.completeButtonMessage || (
                <FormattedMessage
                  id="a5823a1b-7f91-48e0-8d3c-c49d2200d8e5"
                  defaultMessage="Save and Exit"
                />
              )}
            </Button>
          )}

          <Button
            onClick={handleSubmit(this.updateTemplate)}
            isLoading={isSaving || isProcessing}
            disabled={isSaving || isProcessing}
            automationId="save-template-button"
            className="OrganizationTemplateEdit--Uploader--NextButton"
            variant="primary"
            buttonColor="action"
          >
            {this.props.completeButtonMessage || (
              <FormattedMessage
                id="a4823a1b-7f91-48e0-8d3c-c49d2200d8e5"
                defaultMessage="Prepare Document"
              />
            )}
          </Button>
        </div>
      </form>
    );
  }
}

OrganizationTemplateEditForm.propTypes = {
  template: TEMPLATE_PROP_TYPE,
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    lenderAccess: PropTypes.bool,
  }),

  updateTemplateMutateFn: PropTypes.func.isRequired,
  deleteTemplateMutateFn: PropTypes.func.isRequired,
  // Use predefined permalink
  presetPermalink: PropTypes.string,
  hideCancel: PropTypes.bool,
  hideSaveAndExit: PropTypes.bool,

  completeFn: PropTypes.func,
  completeButtonMessage: PropTypes.object,

  quietUpdate: PropTypes.bool,
};

export default reduxForm({
  form: "smbEditTemplate",
  validate,
})((props) => (
  <OrganizationTemplateEditForm
    {...props}
    navigate={useNavigate()}
    location={useLocation()}
    esignAuthEnabled={useFeatureFlag(PROOF_TRANSACTIONS)}
  />
));
